import { EnvironmentGroupId } from '@/react/portainer/environments/types';
import { Gpu } from '@/react/docker/host/SetupView/GpusList';

import { Directive, ChangeWindow } from '../../../types';

/**
 * Complete form values for Docker Setup Policy form.
 * Combines common policy fields (name, environmentGroupIds) with type-specific fields.
 * This is the type used by Formik in DockerSetupPolicyForm.
 */
export interface DockerSetupFormValues {
  /** Policy name */
  name: string;
  /** Environment groups to apply the policy to */
  environmentGroupIds: Array<EnvironmentGroupId>;
  /** Enable host management features (CPU/memory stats, etc.) */
  enableHostManagementFeatures: boolean;
  /** Allow regular users to browse volumes */
  allowVolumeBrowserForRegularUsers: boolean;
  /** Enable GPU management support */
  enableGpuManagement: boolean;
  /** Enable image update notifications */
  enableImageNotification: boolean;
  /** List of available GPUs */
  gpus: Array<Gpu>;
  /** GitOps change window configuration */
  changeWindow: ChangeWindow;
}

/**
 * Type-specific form data for Docker Setup Policy (without common fields).
 * Used by inner form components.
 */
export interface DockerSetupFormData {
  /** Enable host management features (CPU/memory stats, etc.) */
  enableHostManagementFeatures: boolean;
  /** Allow regular users to browse volumes */
  allowVolumeBrowserForRegularUsers: boolean;
  /** Enable GPU management support */
  enableGpuManagement: boolean;
  /** Enable image update notifications */
  enableImageNotification: boolean;
  /** List of available GPUs */
  gpus: Gpu[];
  /** GitOps change window configuration */
  changeWindow: ChangeWindow;
}

/**
 * Stored/API payload for Docker Setup Policy
 * Uses camelCase for all fields
 */
export interface DockerSetupPolicyPayload {
  enableHostManagementFeatures: Directive<boolean>;
  allowVolumeBrowserForRegularUsers: Directive<boolean>;
  enableGpuManagement: Directive<boolean>;
  enableImageNotification: Directive<boolean>;
  gpus: Directive<Gpu[]>;
  endpointChangeWindow: Directive<ChangeWindow>;
}

/**
 * Default initial values for Docker Setup Policy type-specific fields (create mode)
 */
export const DOCKER_SETUP_INITIAL_VALUES: DockerSetupFormData = {
  enableHostManagementFeatures: false,
  allowVolumeBrowserForRegularUsers: false,
  enableGpuManagement: false,
  enableImageNotification: false,
  gpus: [],
  changeWindow: {
    Enabled: false,
    StartTime: '22:00',
    EndTime: '02:00',
  },
};

/**
 * Default initial values for complete Docker Setup Policy form (create mode)
 */
export const DOCKER_SETUP_FORM_INITIAL_VALUES: DockerSetupFormValues = {
  name: '',
  environmentGroupIds: [],
  ...DOCKER_SETUP_INITIAL_VALUES,
};
